// RetoursAchatMobile.jsx — onglet « Retour d'achat » en version mobile, CRUD complet.
// Réutilise le singleton retourAchatAPI et le PaymentFormModal de GestionRetourAchat.tsx.
//
// Comme le desktop, les lignes proviennent exclusivement des articles de la commande
// d'achat d'origine : ni scanner, ni recherche produit, ni ajout hors commande.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { message, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, SearchBar, Chips, EmptyState, Fab, Sheet, ActionRow,
  DetailRow, Note, Field, TextInput, TextAreaInput, SelectInput, Pill, Mono, Ico,
  fmt, fmtMoney, useMobileMenu,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import {
  retourAchatAPI, PaymentFormModal,
} from "../../gestion-retour-achat/GestionRetourAchat";
import { PURCHASE_TABS } from "../../gestion-achat/PurchaseTabsLayout";
import {
  PERM, computeTotals, destructiveActionsFor, fmtDate, isEditBlocked,
  MIN_RETURN_QUANTITY, num, orderPill, parseDecimal, paymentPill, paymentStatusOf,
} from "./retoursMobileShared";

const PAGE = 20;

const FILTERS = [
  { key: "all", label: "Tous", status: null },
  { key: "unpaid", label: "Non payés", status: "Non payé" },
  { key: "partially_paid", label: "Partiels", status: "Partiellement payé" },
  { key: "paid", label: "Payés", status: "Payé" },
];

const idOf = (v) => (v && typeof v === "object" ? v.id : v);
const todayIso = () => new Date().toISOString().slice(0, 10);

// retourAchatAPI.fetchOrderItems renvoie `response.data` brut : tableau ou { items }.
const asItems = (d) => (Array.isArray(d) ? d : d?.items || []);

export default function RetoursAchatMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();

  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);

  const can = {
    create: hasPermission(PERM.create),
    edit: hasPermission(PERM.edit),
    delete: hasPermission(PERM.delete),
  };

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const debounceRef = useRef(null);

  const [navOpen, setNavOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paymentFor, setPaymentFor] = useState(null);

  /* ---------- formulaire ---------- */
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // retour en cours d'édition
  const [suppliers, setSuppliers] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [originalOrders, setOriginalOrders] = useState([]);
  const [form, setForm] = useState(null);         // {supplier_id, original_order_id, order_date, order_status, tax_id, terms_condition, remarks}
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);

  const otherTabs = useMemo(
    () => PURCHASE_TABS.filter((t) => hasPermission(t.permission) && !t.path.endsWith("/retour-achat")),
    [hasPermission]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAppliedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const load = useCallback(
    async (p) => {
      if (!warehouseId) { setRows([]); setTotal(0); return; }
      const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
      setLoading(true);
      try {
        const res = await retourAchatAPI.fetchRetourAchats({
          page: p,
          limit: PAGE,
          warehouse: warehouseId,
          company_id: companyId,
          ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
          ...(f.status ? { payment_status: f.status } : {}),
        });
        const incoming = res.retourAchats || [];
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(res.totalItems || 0);
      } catch {
        message.error("Impossible de charger les retours d'achat");
      } finally {
        setLoading(false);
      }
    },
    [warehouseId, companyId, appliedSearch, filter]
  );

  useEffect(() => { setPage(1); load(1); }, [load]);

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };
  const reload = () => { setPage(1); load(1); };

  // Référentiels du formulaire
  useEffect(() => {
    if (!warehouseId) return;
    let alive = true;
    Promise.all([
      retourAchatAPI.fetchFournisseurs({ warehouse: warehouseId }).catch(() => []),
      retourAchatAPI.fetchTaxes().catch(() => []),
    ]).then(([s, t]) => {
      if (!alive) return;
      setSuppliers(Array.isArray(s) ? s : []);
      setTaxes(Array.isArray(t) ? t : []);
    });
    return () => { alive = false; };
  }, [warehouseId]);

  /* ---------- détail ---------- */
  const openDetail = async (record) => {
    setDetail(record);
    setItems([]); setPayments([]);
    setDetailLoading(true);
    try {
      const [lignes, pays] = await Promise.all([
        retourAchatAPI.fetchOrderItems(record.id).catch(() => []),
        axios
          .get("/api/payments", {
            params: { payment_type: "in", order_id: record.id, warehouse_id: record.warehouse_id },
          })
          .then((r) => (Array.isArray(r.data) ? r.data : r.data?.payments || []))
          .catch(() => []),
      ]);
      setItems(asItems(lignes));
      setPayments(pays);
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setDetail(null); setItems([]); setPayments([]); };

  /* ---------- actions destructives ---------- */
  const runAction = (record, kind) => {
    const spec = {
      restore: {
        title: "Restaurer ce retour d'achat ?", ok: "Oui, restaurer", danger: false,
        run: () => retourAchatAPI.restoreRetourAchat(record.id), done: "Retour restauré avec succès",
      },
      cancel: {
        title: "Annuler ce retour d'achat ?", ok: "Oui, annuler", danger: true,
        run: () => retourAchatAPI.cancelRetourAchat(record.id), done: "Retour annulé avec succès",
      },
      delete: {
        title: "Supprimer ce retour d'achat ?", ok: "Oui, supprimer", danger: true,
        run: () => retourAchatAPI.deleteRetourAchat(record.id), done: "Retour supprimé avec succès",
      },
    }[kind];
    if (!spec) return;

    closeDetail();
    Modal.confirm({
      title: spec.title, centered: true, okText: spec.ok, cancelText: "Non",
      okType: spec.danger ? "danger" : "primary",
      onOk: async () => {
        try { await spec.run(); message.success(spec.done); reload(); }
        catch { message.error("L'opération a échoué"); }
      },
    });
  };

  /* ---------- formulaire ---------- */
  const loadOriginalOrders = async (supplierId) => {
    if (!supplierId) { setOriginalOrders([]); return; }
    try {
      const orders = await retourAchatAPI.fetchAchatsForReturn(Number(supplierId), Number(warehouseId));
      setOriginalOrders(Array.isArray(orders) ? orders : []);
    } catch {
      setOriginalOrders([]);
    }
  };

  // Le desktop pré-remplit chaque ligne avec la quantité ACHETÉE et plafonne à cette
  // valeur : le déjà-retourné n'est pas déduit (GestionRetourAchat.tsx:2278). Reproduit tel quel.
  const loadOrderItems = async (orderId) => {
    if (!orderId) { setLines([]); return; }
    try {
      const res = await axios.get(`/api/orders/${orderId}/items`);
      setLines(
        asItems(res.data).map((it) => ({
          product_id: it.product_id,
          product_name: it.product_name || it.name || "Produit",
          quantity: num(it.quantity),
          max_quantity: num(it.quantity),
          unit_price: num(it.unit_price),
          discount_rate: num(it.discount_rate),
          tax_rate: num(it.tax_rate),
          tax_amount: 0,
          subtotal: 0,
          original_order_item_id: it.id,
        }))
      );
    } catch {
      message.error("Impossible de charger les articles de la commande d'origine");
      setLines([]);
    }
  };

  const openCreate = () => {
    if (!warehouseId) return message.warning("Veuillez sélectionner un établissement");
    setEditing(null);
    setForm({
      supplier_id: "", original_order_id: "", order_date: todayIso(),
      order_status: "Terminé", tax_id: "", terms_condition: "", remarks: "",
    });
    setLines([]); setOriginalOrders([]);
    setFormOpen(true);
  };

  const openEdit = async (record) => {
    closeDetail();
    setEditing(record);
    setForm({
      supplier_id: String(record.user_id ?? ""),
      original_order_id: String(record.original_order_id ?? ""),
      order_date: String(record.order_date || todayIso()).slice(0, 10),
      order_status: record.order_status || "Terminé",
      tax_id: record.tax_id ? String(record.tax_id) : "",
      terms_condition: record.terms_condition || "",
      remarks: record.notes || "",
    });
    setFormOpen(true);

    await loadOriginalOrders(record.user_id);
    // Les lignes du retour existant, plafonnées par les quantités de la commande d'origine.
    try {
      const [currentRaw, original] = await Promise.all([
        retourAchatAPI.fetchOrderItems(record.id).catch(() => []),
        record.original_order_id
          ? axios.get(`/api/orders/${record.original_order_id}/items`)
              .then((r) => asItems(r.data))
              .catch(() => [])
          : Promise.resolve([]),
      ]);
      setLines(
        asItems(currentRaw).map((it) => {
          const src = original.find((o) => o.id === it.original_order_item_id);
          return {
            id: it.id,
            product_id: it.product_id,
            product_name: it.product_name || it.name || "Produit",
            quantity: num(it.quantity),
            max_quantity: src ? num(src.quantity) : num(it.quantity),
            unit_price: num(it.unit_price),
            discount_rate: num(it.discount_rate),
            tax_rate: num(it.tax_rate),
            tax_amount: num(it.tax_amount),
            subtotal: num(it.subtotal),
            original_order_item_id: it.original_order_item_id,
          };
        })
      );
    } catch {
      setLines([]);
    }
  };

  const closeForm = () => { if (!saving) { setFormOpen(false); setEditing(null); setLines([]); } };

  const globalTax = useMemo(
    () => (form?.tax_id ? taxes.find((t) => String(t.id) === String(form.tax_id)) || null : null),
    [form?.tax_id, taxes]
  );
  const totals = useMemo(() => computeTotals(lines, globalTax), [lines, globalTax]);

  const setLineField = (index, key, raw) => {
    setLines((prev) => {
      const next = [...prev];
      const line = { ...next[index] };
      if (key === "quantity") {
        let q = parseDecimal(raw);
        if (q > line.max_quantity) {
          message.warning(`La quantité ne peut pas dépasser ${fmt(line.max_quantity)} (quantité achetée)`);
          q = line.max_quantity;
        }
        line.quantity = Math.max(MIN_RETURN_QUANTITY, q);
      } else if (key === "discount_rate") {
        line.discount_rate = Math.min(100, Math.max(0, parseDecimal(raw)));
      } else if (key === "tax_rate") {
        line.tax_rate = parseDecimal(raw);
      } else {
        line[key] = Math.max(0, parseDecimal(raw));
      }
      next[index] = line;
      return next;
    });
  };

  const removeLine = (index) => setLines((prev) => prev.filter((_, i) => i !== index));

  const formValid = Boolean(form?.supplier_id) && Boolean(form?.original_order_id) && lines.length > 0;

  const submit = async () => {
    if (!formValid) return message.error("Sélectionnez une commande d'origine et au moins un article");
    if (submitting.current) return;
    submitting.current = true;
    setSaving(true);

    const computed = totals.lines;
    const paid = editing ? num(editing.paid_amount) : 0;

    const orderData = {
      company_id: companyId,
      order_type: "purchase_return",
      user_id: Number(form.supplier_id),
      warehouse_id: warehouseId,
      order_date: form.order_date,
      order_status: form.order_status,
      discount: totals.discountAmount,
      tax_id: globalTax ? globalTax.id : null,
      tax_rate: globalTax ? num(globalTax.rate) : null,
      tax_amount: totals.taxAmount,
      subtotal: totals.subTotal,
      total: totals.grandTotal,
      paid_amount: paid,
      due_amount: totals.grandTotal - paid,
      payment_status: editing ? paymentStatusOf(paid, totals.grandTotal) : "Non payé",
      terms_condition: form.terms_condition || "",
      notes: form.remarks || "",
      staff_user_id: null,
      total_items: computed.length,
      total_quantity: computed.reduce((s, l) => s + num(l.quantity), 0),
      original_order_id: Number(form.original_order_id),
      items: computed.map((l) => ({
        product_id: l.product_id,
        quantity: l.quantity,
        unit_price: l.unit_price,
        discount_rate: l.discount_rate,
        tax_rate: l.tax_rate,
        tax_amount: l.tax_amount,
        subtotal: l.subtotal,
        original_order_item_id: l.original_order_item_id,
        id: editing && l.id ? l.id : undefined,
      })),
    };

    try {
      if (editing) {
        await retourAchatAPI.updateRetourAchat(editing.id, orderData);
        message.success("Retour d'achat mis à jour avec succès");
      } else {
        await retourAchatAPI.createRetourAchat(orderData);
        message.success("Retour d'achat créé avec succès");
      }
      setFormOpen(false); setEditing(null); setLines([]);
      reload();
    } catch (err) {
      message.error(err?.response?.data?.error || "Erreur lors de l'enregistrement du retour");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  /* ---------- rendu ---------- */
  if (!warehouseId) {
    return (
      <ProductScreen>
        <Appbar title="Retours d'achat" onMenu={onMenu} />
        <div className="body">
          <EmptyState icon="warehouse" title="Aucun établissement sélectionné" text="Choisissez un établissement pour voir les retours d'achat." />
        </div>
      </ProductScreen>
    );
  }

  const actions = detail ? destructiveActionsFor(detail, can.delete) : {};
  const dueAmount = detail ? num(detail.total) - num(detail.paid_amount) : 0;

  return (
    <ProductScreen>
      <Appbar
        title="Retours d'achat" onMenu={onMenu}
        subtitle={`${fmt(total)} retour${total > 1 ? "s" : ""}`}
        action={
          otherTabs.length ? (
            <button className="iconbtn" aria-label="Aller à un autre onglet" onClick={() => setNavOpen(true)}>
              <Ico name="list-ordered" />
            </button>
          ) : null
        }
      />

      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un n° de facture…" />
        <Chips value={filter} onChange={setFilter} options={FILTERS.map((f) => ({ key: f.key, label: f.label }))} />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement des retours" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="arrow-left"
            title={appliedSearch || filter !== "all" ? "Aucun résultat" : "Aucun retour d'achat"}
            text={appliedSearch || filter !== "all"
              ? "Aucun retour ne correspond à vos filtres."
              : "Créez un retour à partir d'une commande d'achat."}
          />
        ) : (
          <div className="list">
            {rows.map((r) => {
              const pay = paymentPill(r.payment_status);
              const ord = orderPill(r);
              return (
                <button key={r.id} className="rcard" onClick={() => openDetail(r)}>
                  <Mono name={r.invoice_number || "R"} />
                  <div className="rcard__main">
                    <div className="rcard__name">{r.invoice_number}</div>
                    <div className="rcard__meta">
                      {r.is_deleted === 1 ? <Pill tone="danger" label="Supprimé" /> : null}
                      <Pill tone={pay.tone} label={pay.label} />
                      <Pill tone={ord.tone} label={ord.label} />
                    </div>
                    <div className="rcard__meta">
                      <span className="rcard__sub">{fmtDate(r.order_date)} · {r.user_name || "Fournisseur"}</span>
                    </div>
                  </div>
                  <span className="rcard__price">
                    {fmtMoney(r.total)}
                    <small> payé {fmtMoney(r.paid_amount)}</small>
                  </span>
                </button>
              );
            })}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={openCreate} label="Nouveau retour" /> : null}

      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…" subtitle="Autres écrans du module Achats.">
        {otherTabs.map((t) => (
          <ActionRow key={t.key} icon="chevron-right" label={t.label}
            onClick={() => { setNavOpen(false); navigate(t.path); }} />
        ))}
      </Sheet>

      {/* feuille : détail */}
      <Sheet open={Boolean(detail)} onClose={closeDetail} title={detail?.invoice_number} subtitle={detail?.user_name}>
        {detail ? (
          <>
            <DetailRow label="Date" value={fmtDate(detail.order_date)} />
            <DetailRow label="Établissement" value={detail.warehouse_name || "—"} />
            <DetailRow label="Statut" value={orderPill(detail).label} />
            <DetailRow label="Statut paiement" value={paymentPill(detail.payment_status).label} />
            <DetailRow label="Total" value={fmtMoney(detail.total)} />
            <DetailRow label="Payé" value={fmtMoney(detail.paid_amount)} />
            <DetailRow label="Restant" value={fmtMoney(Math.max(0, dueAmount))} />

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Articles</div>
            {detailLoading && !items.length ? (
              <EmptyState loading title="Chargement…" />
            ) : !items.length ? (
              <Note>Aucun article à afficher.</Note>
            ) : (
              items.map((it, i) => (
                <DetailRow key={it.id ?? i} label={it.product_name || it.name}
                  value={`${fmt(it.quantity)} × ${fmtMoney(it.unit_price)}`} />
              ))
            )}

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Paiements</div>
            {!payments.length ? (
              <Note>Aucun paiement enregistré pour ce retour.</Note>
            ) : (
              payments.map((p, i) => (
                <DetailRow key={p.id ?? i}
                  label={`${p.payment_number || `Paiement ${i + 1}`}${p.payment_mode_name ? ` · ${p.payment_mode_name}` : ""}`}
                  value={fmtMoney(p.amount)} />
              ))
            )}

            <div style={{ marginTop: 16 }}>
              {dueAmount > 0 && detail.order_status !== "Annulé" && detail.is_deleted !== 1 ? (
                <ActionRow icon="banknote" label="Ajouter un paiement" onClick={() => setPaymentFor(detail)} />
              ) : null}
              {can.edit && !isEditBlocked(detail) ? (
                <ActionRow icon="pencil" label="Modifier le retour" onClick={() => openEdit(detail)} />
              ) : null}
              {actions.restore ? (
                <ActionRow icon="arrow-left" label="Restaurer le retour" onClick={() => runAction(detail, "restore")} />
              ) : null}
              {actions.cancel ? (
                <ActionRow icon="ban" label="Annuler le retour" danger onClick={() => runAction(detail, "cancel")} />
              ) : null}
              {actions.delete ? (
                <ActionRow icon="trash-2" label="Supprimer le retour" danger onClick={() => runAction(detail, "delete")} />
              ) : null}
            </div>
          </>
        ) : null}
      </Sheet>

      {/* feuille : formulaire */}
      <Sheet
        open={formOpen} onClose={closeForm}
        title={editing ? "Modifier le retour" : "Nouveau retour d'achat"}
        subtitle="Les articles proviennent de la commande d'achat d'origine."
      >
        {form ? (
          <>
            <Field label="Fournisseur *">
              <SelectInput
                value={form.supplier_id}
                onChange={(v) => {
                  setForm((f) => ({ ...f, supplier_id: v || "", original_order_id: "" }));
                  setLines([]);
                  loadOriginalOrders(v);
                }}
                options={suppliers.map((s) => ({ value: String(s.id), label: s.name }))}
                placeholder="Sélectionner un fournisseur"
              />
            </Field>

            <Field label="Commande d'origine *">
              <SelectInput
                value={form.original_order_id}
                onChange={(v) => { setForm((f) => ({ ...f, original_order_id: v || "" })); loadOrderItems(v); }}
                options={originalOrders.map((o) => ({
                  value: String(o.id),
                  label: `${o.invoice_number} — ${fmtDate(o.order_date)} — ${fmtMoney(o.total)}`,
                }))}
                placeholder={form.supplier_id ? "Sélectionner une commande" : "Choisissez d'abord un fournisseur"}
              />
            </Field>

            <Field label="Date du retour">
              <div className="ctrl">
                <input type="date" value={form.order_date}
                  onChange={(e) => setForm((f) => ({ ...f, order_date: e.target.value }))} />
              </div>
            </Field>

            <Field label="Taxe globale" hint="écrase la taxe de chaque ligne">
              <SelectInput
                value={form.tax_id}
                onChange={(v) => setForm((f) => ({ ...f, tax_id: v || "" }))}
                options={taxes.map((t) => ({ value: String(t.id), label: `${t.name} (${t.rate} %)` }))}
                placeholder="Aucune (taxes par ligne)"
              />
            </Field>

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>
              Articles retournés {lines.length ? <small>({lines.length})</small> : null}
            </div>
            {!lines.length ? (
              <Note>Sélectionnez une commande d'origine pour charger ses articles.</Note>
            ) : (
              lines.map((line, i) => {
                const c = totals.lines[i];
                return (
                  <div key={line.original_order_item_id ?? i} className="preview" style={{ display: "block", marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div className="preview__name">{line.product_name}</div>
                      <button className="iconbtn" aria-label="Retirer" onClick={() => removeLine(i)}>
                        <Ico name="trash-2" />
                      </button>
                    </div>
                    <Field label={`Quantité (max ${fmt(line.max_quantity)})`}>
                      <TextInput value={String(line.quantity)} inputMode="decimal"
                        onChange={(v) => setLineField(i, "quantity", v)} />
                    </Field>
                    <Field label="Prix unitaire">
                      <TextInput value={String(line.unit_price)} inputMode="decimal"
                        onChange={(v) => setLineField(i, "unit_price", v)} />
                    </Field>
                    <Field label="Remise (%)">
                      <TextInput value={String(line.discount_rate)} inputMode="decimal"
                        onChange={(v) => setLineField(i, "discount_rate", v)} />
                    </Field>
                    {!globalTax ? (
                      <Field label="Taxe de la ligne">
                        <SelectInput
                          value={String(line.tax_rate)}
                          onChange={(v) => setLineField(i, "tax_rate", v ?? 0)}
                          options={[
                            { value: "0", label: "Aucune (0 %)" },
                            ...taxes.map((t) => ({ value: String(t.rate), label: `${t.name} (${t.rate} %)` })),
                          ]}
                        />
                      </Field>
                    ) : null}
                    <DetailRow label="Sous-total" value={fmtMoney(c?.subtotal)} />
                  </div>
                );
              })
            )}

            {lines.length ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Totaux</div>
                <DetailRow label="Total HT" value={fmtMoney(totals.subTotal)} />
                <DetailRow label="Total remises" value={fmtMoney(totals.discountAmount)} />
                <DetailRow label="Total taxes" value={fmtMoney(totals.taxAmount)} />
                <DetailRow label="Total TTC" value={fmtMoney(totals.grandTotal)} />
              </>
            ) : null}

            <Field label="Conditions" hint="facultatif">
              <TextAreaInput value={form.terms_condition}
                onChange={(v) => setForm((f) => ({ ...f, terms_condition: v }))} rows={2} />
            </Field>
            <Field label="Remarques" hint="facultatif">
              <TextAreaInput value={form.remarks}
                onChange={(v) => setForm((f) => ({ ...f, remarks: v }))} rows={2} />
            </Field>

            <div className="sheet__actions">
              <button className="btn btn--ghost" onClick={closeForm} disabled={saving}>Annuler</button>
              <button className="btn btn--primary" onClick={submit} disabled={saving || !formValid}>
                <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
                {editing ? "Mettre à jour" : "Créer le retour"}
              </button>
            </div>
          </>
        ) : null}
      </Sheet>

      <PaymentFormModal
        visible={Boolean(paymentFor)}
        order={paymentFor}
        onClose={() => setPaymentFor(null)}
        onPaymentAdded={() => { setPaymentFor(null); closeDetail(); reload(); }}
      />
    </ProductScreen>
  );
}
